import { useEffect, useState } from 'react';
import API from '../api';
import { usePermissions } from '../hooks/usePermissions';
import { logDownload } from '../utils/eventLog';
import FileDropZone from '../components/FileDropZone';

// 연락처관리(명함) — 영업관리 > 업체관리 아래 신규 메뉴(2026-09).
// 명함 앞/뒤 사진을 업로드하면 비전 AI(backend/utils/businessCardOcr.js)가 회사명/부서/직급/
// 이름/이메일/주소/홈페이지/연락처를 인식해 전자명함으로 저장한다. 회사명·이름은 한글→영문→
// 한자 순으로 카드 앞면에 표시하고(요청사항 4), 뒷면이 있으면 클릭 시 모달 한 화면에 앞/뒤를
// 함께 보여준다(요청사항 5). 가로/세로 명함은 orientation 값에 따라 카드 비율을 다르게 표시한다
// (요청사항 8).

// 2026-09(2차) — 상단 탭(분류)이 하드코딩에서 "관리항목" 버튼으로 사용자가 직접 관리하는
// 방식(contact_menu_tabs 테이블, 자산관리 관리항목과 동일한 패턴)으로 바뀌었다. 아래는 서버
// 응답이 오기 전 첫 렌더/네트워크 실패 시에만 쓰이는 폴백 값이다.
const FALLBACK_TABS = [
  { id: 'fallback-1', name: '생산업체', color: '#185FA5', bg_color: '#E6F1FB' },
  { id: 'fallback-2', name: '제작업체', color: '#7C3AED', bg_color: '#F3E8FF' },
  { id: 'fallback-3', name: '고객사',   color: '#0F6E56', bg_color: '#E1F5EE' },
  { id: 'fallback-4', name: '협력사',   color: '#B45309', bg_color: '#FEF3C7' },
  { id: 'fallback-5', name: '기타',     color: '#666666', bg_color: '#f0f0f0' },
];
const FALLBACK_STYLE = { color: '#666', bg: '#f0f0f0' };
const styleOfTab = (tabs, cat) => {
  const t = tabs.find(t => t.name === cat);
  return t ? { color: t.color, bg: t.bg_color } : FALLBACK_STYLE;
};

// ── 인증(로그인) 필요한 이미지 — <img src="...">로 직접 접근할 수 없으므로(Authorization 헤더가
// 안 실림), HRManagement.js의 AuthedPhoto 패턴과 동일하게 blob으로 받아 로컬 objectURL로 표시한다.
function AuthedImage({ filename, alt, style, placeholder }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!filename) { setUrl(null); return undefined; }
    let objUrl = null;
    let cancelled = false;
    API.get(`/contacts/image/${encodeURIComponent(filename)}`, { responseType: 'blob' })
      .then(r => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(r.data);
        setUrl(objUrl);
      })
      .catch(() => {});
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [filename]);
  if (!url) return placeholder !== undefined ? placeholder : (
    <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 12, background: '#fafafa' }}>이미지 없음</div>
  );
  return <img src={url} alt={alt || ''} style={style} />;
}

function downloadContactImage(filename, oriName) {
  API.get(`/contacts/image/${encodeURIComponent(filename)}`, { responseType: 'blob' })
    .then(res => {
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = oriName || filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      logDownload('명함관리', oriName || filename);
    })
    .catch(() => alert('다운로드에 실패했습니다.'));
}

// 명함 앞면 카드 썸네일 — orientation에 따라 가로/세로 비율을 바꾼다(요청사항 8).
// 표기 순서: 한글 → 영문 → 한자(요청사항 4).
function CardThumb({ card, onClick, tabs }) {
  const landscape = card.orientation !== 'portrait';
  const boxStyle = landscape
    ? { width: 250, height: 148 }
    : { width: 148, height: 250 };
  const st = styleOfTab(tabs, card.category);
  return (
    <div onClick={onClick} style={{
      border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
      background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column',
      transition: 'box-shadow 0.15s', width: boxStyle.width,
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.12)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'}>
      <div style={{ ...boxStyle, position: 'relative', background: '#f3f4f6' }}>
        <AuthedImage filename={card.front_image_name} alt="명함 앞면" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <span style={{
          position: 'absolute', top: 6, left: 6, fontSize: 11, fontWeight: 700, padding: '2px 8px',
          borderRadius: 999, color: st.color, background: st.bg,
        }}>{card.category || '기타'}</span>
        {card.back_image_name && (
          <span style={{ position: 'absolute', bottom: 6, right: 6, fontSize: 10, color: '#fff', background: 'rgba(0,0,0,0.55)', borderRadius: 999, padding: '2px 7px' }}>앞/뒤</span>
        )}
      </div>
      <div style={{ padding: '8px 10px', fontSize: 12, lineHeight: 1.5, color: '#333' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#185FA5', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {card.company_name || card.company_name_en || card.company_name_hanja || '(회사명 미확인)'}
        </div>
        {(card.company_name_en || card.company_name_hanja) && (
          <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {[card.company_name_en, card.company_name_hanja].filter(Boolean).join(' · ')}
          </div>
        )}
        <div style={{ marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {card.name_kr || card.name_en || card.name_hanja || '(이름 미확인)'}
          {card.position ? ` · ${card.position}` : ''}
        </div>
      </div>
    </div>
  );
}

// Companies.js(업체관리)의 CompanyForm 입력폼과 동일한 규격(grid + labelStyle/inputStyle)으로
// 통일한 명함 필드 입력폼(등록/수정 공용) — 한글/영문/한자 표기를 각각 별도 입력칸으로 둔다.
const formLabelStyle = { fontSize: 13, color: '#555', fontWeight: 500, display: 'block', marginBottom: 4 };
const formInputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 12, outline: 'none' };

// 신뢰도 기반 UI(사용자 요청사항 5) — OCR/AI가 낮은 신뢰도로 뽑아낸 필드는 붉은 테두리로
// 표시해, 사용자가 저장 전 1초 만에 어디를 확인해야 할지 알 수 있게 한다. confidence 맵의
// 값이 숫자이고 임계값 미만일 때만 표시하고(null은 "OCR로 확인 불가"일 뿐 반드시 틀렸다는
// 뜻은 아니므로, 과도한 경고를 피하기 위해 중립으로 둔다), 값 자체가 빈 문자열이면 굳이
// 붉게 표시하지 않는다(빈 값은 입력이 필요하다는 게 이미 눈에 보임).
const LOW_CONFIDENCE_THRESHOLD = 60;
function lowConfidenceStyle(confidence, key, value) {
  if (!value) return {};
  const c = confidence && confidence[key];
  if (typeof c !== 'number') return {};
  if (c >= LOW_CONFIDENCE_THRESHOLD) return {};
  return { border: '1.5px solid #ef4444', background: '#fff5f5' };
}

function CardFieldsForm({ value, onChange, categories, confidence }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const catNames = (categories && categories.length) ? categories.map(t => t.name) : FALLBACK_TABS.map(t => t.name);
  const cs = (key) => ({ ...formInputStyle, ...lowConfidenceStyle(confidence, key, value[key]) });
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={formLabelStyle}>관리항목(분류)</label>
          <select value={value.category || '기타'} onChange={set('category')} style={formInputStyle}>
            {catNames.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={formLabelStyle}>서비스항목</label>
          <input style={cs('service_type')} value={value.service_type || ''} onChange={set('service_type')} placeholder="명판, 자재도소매, 용품 등" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <div>
          <label style={formLabelStyle}>회사명(한글)</label>
          <input style={cs('company_name')} value={value.company_name || ''} onChange={set('company_name')} />
        </div>
        <div>
          <label style={formLabelStyle}>회사명(영문)</label>
          <input style={cs('company_name_en')} value={value.company_name_en || ''} onChange={set('company_name_en')} />
        </div>
        <div>
          <label style={formLabelStyle}>회사명(한자)</label>
          <input style={cs('company_name_hanja')} value={value.company_name_hanja || ''} onChange={set('company_name_hanja')} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <div>
          <label style={formLabelStyle}>이름(한글)</label>
          <input style={cs('name_kr')} value={value.name_kr || ''} onChange={set('name_kr')} />
        </div>
        <div>
          <label style={formLabelStyle}>이름(영문)</label>
          <input style={cs('name_en')} value={value.name_en || ''} onChange={set('name_en')} />
        </div>
        <div>
          <label style={formLabelStyle}>이름(한자)</label>
          <input style={cs('name_hanja')} value={value.name_hanja || ''} onChange={set('name_hanja')} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={formLabelStyle}>부서</label>
          <input style={cs('department')} value={value.department || ''} onChange={set('department')} />
        </div>
        <div>
          <label style={formLabelStyle}>직급</label>
          <input style={cs('position')} value={value.position || ''} onChange={set('position')} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={formLabelStyle}>이메일</label>
          <input style={cs('email')} value={value.email || ''} onChange={set('email')} />
        </div>
        <div>
          <label style={formLabelStyle}>전화(대표/직통)</label>
          <input style={cs('phone')} value={value.phone || ''} onChange={set('phone')} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={formLabelStyle}>휴대폰</label>
          <input style={cs('mobile')} value={value.mobile || ''} onChange={set('mobile')} />
        </div>
        <div>
          <label style={formLabelStyle}>팩스</label>
          <input style={cs('fax')} value={value.fax || ''} onChange={set('fax')} />
        </div>
      </div>

      <label style={formLabelStyle}>회사주소</label>
      <input style={cs('address')} value={value.address || ''} onChange={set('address')} />

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
        <div>
          <label style={formLabelStyle}>홈페이지주소</label>
          <input style={cs('homepage')} value={value.homepage || ''} onChange={set('homepage')} />
        </div>
        <div>
          <label style={formLabelStyle}>가로/세로</label>
          <select value={value.orientation || 'landscape'} onChange={set('orientation')} style={formInputStyle}>
            <option value="landscape">가로명함</option>
            <option value="portrait">세로명함</option>
          </select>
        </div>
      </div>

      <label style={formLabelStyle}>비고</label>
      <textarea value={value.note || ''} onChange={set('note')} style={{ ...formInputStyle, minHeight: 60, resize: 'vertical' }} />
    </div>
  );
}

const EMPTY_FIELDS = {
  category: '기타', service_type: '', company_name: '', company_name_en: '', company_name_hanja: '',
  department: '', position: '', name_kr: '', name_en: '', name_hanja: '',
  email: '', phone: '', mobile: '', fax: '', address: '', homepage: '', note: '', orientation: 'landscape',
};

// ── 단일 등록 모달 — 앞/뒤 1장씩 올려 AI 인식 후 검수/수정하여 저장 ─────────────────
function RegisterModal({ onClose, onSaved, categories }) {
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [recognizing, setRecognizing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  // 신뢰도 기반 UI(요청사항 5) — OCR/AI가 처음 뽑아낸 필드별 신뢰도(0~100 또는 null).
  const [confidence, setConfidence] = useState(null);
  // 저장 시 guess_json으로 함께 보내, 사람이 실제로 무엇을 고쳤는지 서버가 레퍼런스로
  // 학습(ocrReferenceStore)할 수 있게 한다 — OCR/AI가 처음 준 값의 스냅샷(사용자 편집 전).
  const [guessSnapshot, setGuessSnapshot] = useState(null);

  const runOcr = () => {
    if (!frontFile) { setMsg('명함 앞면 이미지를 먼저 선택해주세요.'); return; }
    setRecognizing(true);
    setMsg('');
    const fd = new FormData();
    fd.append('front', frontFile);
    if (backFile) fd.append('back', backFile);
    API.post('/contacts/ocr', fd)
      .then(r => {
        if (r.data?.ok && r.data.fields) {
          const { confidence: conf, ...restFields } = r.data.fields;
          setFields(prev => ({ ...prev, ...restFields }));
          setConfidence(conf || null);
          setGuessSnapshot(restFields);
          // 2026-09(근본원인 수정): 백엔드가 ok:true를 반환해도 실제로는 아무 항목도
          // 인식하지 못했을 수 있다(recognized===false). 이 경우 "인식완료"라는
          // 거짓 성공 메시지 대신 백엔드가 보낸 실제 사유를 그대로 보여준다.
          if (r.data.recognized === false) {
            setMsg(r.data.message || 'AI 자동인식에 실패했습니다. 직접 입력해주세요.');
          } else {
            setMsg('인식이 완료되었습니다. 내용을 확인 후 저장해주세요.');
          }
        } else {
          setMsg(r.data?.message || 'OCR 인식에 실패했습니다. 직접 입력해주세요.');
        }
      })
      .catch(() => setMsg('OCR 인식 중 오류가 발생했습니다. 직접 입력해주세요.'))
      .finally(() => setRecognizing(false));
  };

  const save = () => {
    if (!frontFile) { setMsg('명함 앞면 이미지는 필수입니다.'); return; }
    setSaving(true);
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v || ''));
    if (guessSnapshot) fd.append('guess_json', JSON.stringify(guessSnapshot));
    fd.append('front', frontFile);
    if (backFile) fd.append('back', backFile);
    API.post('/contacts', fd)
      .then(() => { onSaved(); onClose(); })
      .catch(() => setMsg('저장에 실패했습니다.'))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 680, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>명함 등록</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}>✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 4 }}>
          <div>
            <label style={formLabelStyle}>명함 앞면(필수)</label>
            <FileDropZone fileName={frontFile?.name} onFile={setFrontFile} accept="image/*" />
          </div>
          <div>
            <label style={formLabelStyle}>명함 뒷면(선택)</label>
            <FileDropZone fileName={backFile?.name} onFile={setBackFile} accept="image/*" />
          </div>
        </div>

        <button onClick={runOcr} disabled={recognizing || !frontFile} style={{
          padding: '8px 16px', borderRadius: 8, border: 'none', background: '#185FA5', color: '#fff',
          fontWeight: 600, cursor: recognizing ? 'default' : 'pointer', opacity: recognizing ? 0.6 : 1, margin: '14px 0',
        }}>{recognizing ? '인식 중...' : '🔍 AI 자동인식'}</button>
        {msg && <div style={{ fontSize: 12, color: '#185FA5', marginBottom: 10 }}>{msg}</div>}

        <CardFieldsForm value={fields} onChange={setFields} categories={categories} confidence={confidence} />

        <button onClick={save} disabled={saving}
          style={{ width: '100%', padding: '12px', marginTop: 8,
            background: saving ? '#aaa' : '#185FA5', color: '#fff', border: 'none', borderRadius: 8,
            fontSize: 15, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? '저장 중...' : '명함 등록'}
        </button>
      </div>
    </div>
  );
}

// ── 일괄 업로드 모달 — 여러 장을 한 번에 올려 자동으로 명함 단위로 나누어 인식/저장(요청사항 6,7) ──
function BatchUploadModal({ onClose, onSaved }) {
  const [files, setFiles] = useState([]);
  const [pairMode, setPairMode] = useState('pair');
  const [rows, setRows] = useState('');
  const [cols, setCols] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const upload = () => {
    if (!files.length) return;
    setUploading(true);
    setResult(null);
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    fd.append('pairMode', pairMode);
    if (pairMode === 'sheet') {
      if (rows) fd.append('rows', rows);
      if (cols) fd.append('cols', cols);
    }
    API.post('/contacts/batch', fd)
      .then(r => { setResult(r.data); onSaved(); })
      .catch(() => setResult({ ok: false, message: '업로드 중 오류가 발생했습니다.' }))
      .finally(() => setUploading(false));
  };

  // 선택된 파일이 어떤 순번으로 인식될지 미리 보여준다 — 특히 sheet(시트분할) 모드에서는
  // 파일 선택 순서가 결과에 직접 영향을 주므로(홀수번째=앞면시트, 짝수번째=뒷면시트) 사용자가
  // 업로드 전에 순서를 눈으로 확인할 수 있게 한다.
  const orderLabels = files.map((f, i) => {
    if (pairMode === 'single') return `개별 #${i + 1}`;
    if (pairMode === 'sheet') {
      const sheetNo = Math.floor(i / 2) + 1;
      return i % 2 === 0 ? `앞면시트${sheetNo}` : `뒷면시트${sheetNo}`;
    }
    return i % 2 === 0 ? `앞면#${Math.floor(i / 2) + 1}` : `뒷면#${Math.floor(i / 2) + 1}`;
  });

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 640 }}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 16 }}>명함 일괄 업로드</h3>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 10, lineHeight: 1.6 }}>
            여러 장을 한 번에 올리면 자동으로 명함 한 장씩 구분해 인식·저장합니다.<br />
            낱장 스캔 파일이 앞/뒤로 번갈아 올라오는 경우(예: 001,002 / 003,004)는 "앞뒤 쌍으로
            묶기"를, 휴대폰으로 낱장씩 찍은 사진은 "낱장(앞면만)"을, 명함 여러 장이 한 판에 인쇄된
            스캔 시트는 "시트(그리드) 자동분할"을 선택해주세요.
          </div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 13, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input type="radio" checked={pairMode === 'pair'} onChange={() => setPairMode('pair')} />
              앞뒤 쌍으로 묶기(2장씩)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input type="radio" checked={pairMode === 'single'} onChange={() => setPairMode('single')} />
              낱장(앞면만, 1장당 1건)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input type="radio" checked={pairMode === 'sheet'} onChange={() => setPairMode('sheet')} />
              시트(그리드) 자동분할
            </label>
          </div>
          {pairMode === 'sheet' && (
            <div style={{ background: '#EAF3FC', border: '1px solid #cfe0f2', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#185FA5', lineHeight: 1.7 }}>
              파일을 <b>앞면시트1, 뒷면시트1, 앞면시트2, 뒷면시트2 ...</b> 순서로 선택해주세요.
              같은 위치(행·열)에 인쇄된 명함끼리 앞/뒤로 자동으로 짝지어집니다. 뒷면이 없으면
              앞면시트만 올려도 됩니다(마지막 한 장이 남는 경우 앞면 전용으로 처리).
              <div style={{ display: 'flex', gap: 10, marginTop: 8, alignItems: 'center' }}>
                <span>격자 행/열 수동 지정(선택, 자동인식이 잘 안 맞을 때만):</span>
                <input value={rows} onChange={e => setRows(e.target.value.replace(/\D/g, ''))} placeholder="행" style={{ width: 50, padding: '4px 6px', border: '1px solid #ccc', borderRadius: 5 }} />
                <span>×</span>
                <input value={cols} onChange={e => setCols(e.target.value.replace(/\D/g, ''))} placeholder="열" style={{ width: 50, padding: '4px 6px', border: '1px solid #ccc', borderRadius: 5 }} />
              </div>
            </div>
          )}
          <FileDropZone multiple fileNames={files.map(f => f.name)} onFiles={setFiles} accept="image/*" />
          {files.length > 0 && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
              {files.map((f, i) => (
                <div key={i}>{orderLabels[i]} — {f.name}</div>
              ))}
            </div>
          )}
          {result && (
            <div style={{ marginTop: 12, fontSize: 13 }}>
              {result.ok ? (
                <div>
                  <div style={{ color: '#0F6E56', fontWeight: 600 }}>총 {result.total}건 중 {result.created?.length || 0}건 저장 완료{result.failed?.length ? `, ${result.failed.length}건 실패` : ''}.</div>
                  {result.failed?.length > 0 && (
                    <ul style={{ color: '#b91c1c', margin: '6px 0 0', paddingLeft: 18 }}>
                      {result.failed.map((f, i) => <li key={i}>{f.front}: {f.reason}</li>)}
                    </ul>
                  )}
                </div>
              ) : <div style={{ color: '#b91c1c' }}>{result.message}</div>}
            </div>
          )}
        </div>
        <div style={modalFooterStyle}>
          <button onClick={onClose} style={cancelBtnStyle}>닫기</button>
          <button onClick={upload} disabled={uploading || !files.length} style={saveBtnStyle}>
            {uploading ? '업로드/인식 중...' : '업로드 및 인식'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 상세(앞/뒤 모달) — 요청사항 5: "명함카드를 클릭하면 앞면 뒷면이 모달창 한페이지 보여줌" ──
function DetailModal({ card, onClose, onChanged, canWrite, canDelete, canDownload, tabs }) {
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState(card);
  const [newFront, setNewFront] = useState(null);
  const [newBack, setNewBack] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setFields(card); setEditing(false); setNewFront(null); setNewBack(null); }, [card]);

  const landscape = (fields.orientation || card.orientation) !== 'portrait';
  const imgBoxStyle = landscape ? { width: 320, height: 190 } : { width: 190, height: 320 };

  const save = () => {
    setSaving(true);
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
      if (['id', 'front_image_name', 'front_image_ori', 'front_image_path', 'back_image_name', 'back_image_ori', 'back_image_path', 'created_at', 'updated_at', 'created_by', 'raw_text'].includes(k)) return;
      fd.append(k, v || '');
    });
    if (newFront) fd.append('front', newFront);
    if (newBack) fd.append('back', newBack);
    API.put(`/contacts/${card.id}`, fd)
      .then(() => { onChanged(); setEditing(false); })
      .catch(() => alert('저장에 실패했습니다.'))
      .finally(() => setSaving(false));
  };

  const remove = () => {
    if (!window.confirm('이 명함을 삭제하시겠습니까?')) return;
    API.delete(`/contacts/${card.id}`)
      .then(() => { onChanged(); onClose(); })
      .catch(() => alert('삭제에 실패했습니다.'));
  };

  const st = styleOfTab(tabs, card.category);

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 760 }}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 999, color: st.color, background: st.bg, marginRight: 8 }}>{card.category || '기타'}</span>
            명함 상세
          </h3>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>
        <div style={{ padding: 20, maxHeight: '78vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', gap: 20, marginBottom: 18, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>앞면</div>
              <div style={{ ...imgBoxStyle, background: '#f3f4f6', borderRadius: 8, overflow: 'hidden' }}>
                <AuthedImage filename={card.front_image_name} alt="명함 앞면" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              {canDownload && card.front_image_name && (
                <button onClick={() => downloadContactImage(card.front_image_name, card.front_image_ori)} style={smallLinkBtn}>다운로드</button>
              )}
              {editing && (
                <div style={{ marginTop: 6 }}>
                  <FileDropZone fileName={newFront?.name} onFile={setNewFront} accept="image/*" style={{ marginBottom: 0, fontSize: 11, padding: '8px 6px' }} />
                </div>
              )}
            </div>
            {(card.back_image_name || editing) && (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>뒷면</div>
                <div style={{ ...imgBoxStyle, background: '#f3f4f6', borderRadius: 8, overflow: 'hidden' }}>
                  <AuthedImage filename={card.back_image_name} alt="명함 뒷면" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    placeholder={<div style={{ ...imgBoxStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 12 }}>뒷면 없음</div>} />
                </div>
                {canDownload && card.back_image_name && (
                  <button onClick={() => downloadContactImage(card.back_image_name, card.back_image_ori)} style={smallLinkBtn}>다운로드</button>
                )}
                {editing && (
                  <div style={{ marginTop: 6 }}>
                    <FileDropZone fileName={newBack?.name} onFile={setNewBack} accept="image/*" style={{ marginBottom: 0, fontSize: 11, padding: '8px 6px' }} />
                  </div>
                )}
              </div>
            )}
          </div>

          {editing ? (
            <CardFieldsForm value={fields} onChange={setFields} categories={tabs} />
          ) : (
            <div style={{ fontSize: 13, color: '#333', lineHeight: 2 }}>
              <div><b>회사명</b> — {[card.company_name, card.company_name_en, card.company_name_hanja].filter(Boolean).join(' / ') || '-'}</div>
              <div><b>이름</b> — {[card.name_kr, card.name_en, card.name_hanja].filter(Boolean).join(' / ') || '-'} {card.position ? `(${card.position})` : ''}</div>
              <div><b>부서</b> — {card.department || '-'}</div>
              <div><b>서비스항목</b> — {card.service_type || '-'}</div>
              <div><b>이메일</b> — {card.email || '-'}</div>
              <div><b>전화</b> — {card.phone || '-'} {card.mobile ? `/ 휴대폰 ${card.mobile}` : ''} {card.fax ? `/ 팩스 ${card.fax}` : ''}</div>
              <div><b>주소</b> — {card.address || '-'}</div>
              <div><b>홈페이지</b> — {card.homepage || '-'}</div>
              <div><b>비고</b> — {card.note || '-'}</div>
            </div>
          )}
        </div>
        <div style={modalFooterStyle}>
          {canDelete && !editing && <button onClick={remove} style={{ ...cancelBtnStyle, color: '#b91c1c', borderColor: '#fca5a5', marginRight: 'auto' }}>삭제</button>}
          {canWrite && (editing ? (
            <>
              <button onClick={() => setEditing(false)} style={cancelBtnStyle}>취소</button>
              <button onClick={save} disabled={saving} style={saveBtnStyle}>{saving ? '저장 중...' : '저장'}</button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} style={saveBtnStyle}>수정</button>
          ))}
        </div>
      </div>
    </div>
  );
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { background: '#fff', borderRadius: 12, maxWidth: '92vw', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' };
const modalHeaderStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #eee' };
const modalFooterStyle = { display: 'flex', gap: 8, padding: '14px 20px', borderTop: '1px solid #eee', justifyContent: 'flex-end' };
const closeBtnStyle = { border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: '#888' };
const cancelBtnStyle = { padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 };
const saveBtnStyle = { padding: '8px 18px', borderRadius: 8, border: 'none', background: '#185FA5', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13 };
const smallLinkBtn = { display: 'block', marginTop: 4, border: 'none', background: 'none', color: '#185FA5', fontSize: 11, cursor: 'pointer', padding: 0 };

// ── 관리항목(상단 탭) 관리 모달 — 2026-09(2차) 신규. 자산관리 TabManageModal과 동일한
// UX(▲▼ 순서변경/인라인 수정/삭제확인) 패턴을 그대로 따르되, 여기서는 코드접두어·탭유형 대신
// 색상(글자색/배경색)을 관리한다. 삭제 시 그 탭으로 분류된 명함이 있으면 서버가 차단하고
// 건수를 메시지로 알려준다(routes/contacts.js DELETE /categories/:id 참고).
function TabManageModal({ tabs, canManage, onClose, onChanged }) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#185FA5');
  const [newBg, setNewBg] = useState('#E6F1FB');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#185FA5');
  const [editBg, setEditBg] = useState('#E6F1FB');
  const [msg, setMsg] = useState('');
  const sorted = [...tabs].sort((a, b) => a.sort_order - b.sort_order);

  const notify = (e) => { setMsg(e?.response?.data?.message || '오류가 발생했습니다.'); setTimeout(() => setMsg(''), 4000); };

  const addTab = async () => {
    if (!newName.trim()) return;
    try {
      await API.post('/contacts/categories', { name: newName.trim(), color: newColor, bg_color: newBg });
      setNewName(''); onChanged();
    } catch (e) { notify(e); }
  };
  const saveEdit = async (id) => {
    try {
      await API.put(`/contacts/categories/${id}`, { name: editName.trim(), color: editColor, bg_color: editBg });
      setEditingId(null); onChanged();
    } catch (e) { notify(e); }
  };
  const move = async (idx, dir) => {
    const arr = [...sorted];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    try { await API.put('/contacts/categories/reorder', { order: arr.map(t => t.id) }); onChanged(); } catch (e) { notify(e); }
  };
  const remove = async (t) => {
    if (!window.confirm(`"${t.name}" 탭을 삭제하시겠습니까?`)) return;
    try { await API.delete(`/contacts/categories/${t.id}`); onChanged(); } catch (e) { notify(e); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 560 }} onClick={e => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 16 }}>📑 관리항목 — 상단 탭 관리</h3>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
            상단 탭의 이름·색상·순서를 자유롭게 관리할 수 있습니다. 이름을 바꾸면 이미 등록된
            명함들도 함께 새 이름으로 옮겨집니다. 명함이 등록되어 있는 탭은 삭제할 수 없으니
            먼저 다른 분류로 옮겨주세요.
          </div>
          {msg && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{msg}</div>}

          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
            {sorted.length === 0 ? (
              <div style={{ padding: '20px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>탭이 없습니다.</div>
            ) : sorted.map((t, i) => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: i < sorted.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <button onClick={() => move(i, -1)} disabled={!canManage || i === 0} style={{ background: 'none', border: 'none', cursor: canManage ? 'pointer' : 'default', color: '#888', fontSize: 11, lineHeight: 1 }}>▲</button>
                  <button onClick={() => move(i, 1)} disabled={!canManage || i === sorted.length - 1} style={{ background: 'none', border: 'none', cursor: canManage ? 'pointer' : 'default', color: '#888', fontSize: 11, lineHeight: 1 }}>▼</button>
                </div>
                {editingId === t.id ? (
                  <div style={{ display: 'flex', gap: 6, flex: 1, alignItems: 'center' }}>
                    <input autoFocus value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveEdit(t.id)}
                      style={{ flex: 1, padding: '5px 10px', border: '1px solid #185FA5', borderRadius: 6, fontSize: 14 }} />
                    <input type="color" value={editColor} onChange={e => setEditColor(e.target.value)} title="글자색" style={{ width: 30, height: 28, padding: 0, border: '1px solid #ddd', borderRadius: 4 }} />
                    <input type="color" value={editBg} onChange={e => setEditBg(e.target.value)} title="배경색" style={{ width: 30, height: 28, padding: 0, border: '1px solid #ddd', borderRadius: 4 }} />
                  </div>
                ) : (
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: '#222', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {t.name}
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, color: t.color, background: t.bg_color }}>미리보기</span>
                  </span>
                )}
                {canManage && (
                  <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
                    {editingId === t.id ? (
                      <button onClick={() => saveEdit(t.id)} style={{ background: 'none', border: 'none', color: '#185FA5', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0 }}>저장</button>
                    ) : (
                      <button onClick={() => { setEditingId(t.id); setEditName(t.name); setEditColor(t.color || '#185FA5'); setEditBg(t.bg_color || '#E6F1FB'); }}
                        style={{ background: 'none', border: 'none', color: '#555', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0 }}>수정</button>
                    )}
                    <button onClick={() => remove(t)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0 }}>삭제</button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {canManage && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTab()}
                placeholder="새 탭 이름(예: 유통사)" style={{ flex: 1, padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14 }} />
              <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} title="글자색" style={{ width: 34, height: 34, padding: 0, border: '1px solid #ddd', borderRadius: 6 }} />
              <input type="color" value={newBg} onChange={e => setNewBg(e.target.value)} title="배경색" style={{ width: 34, height: 34, padding: 0, border: '1px solid #ddd', borderRadius: 6 }} />
              <button onClick={addTab} disabled={!newName.trim()}
                style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: !newName.trim() ? '#aaa' : '#185FA5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: !newName.trim() ? 'default' : 'pointer' }}>
                + 탭 추가
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Contacts() {
  const [cards, setCards] = useState([]);
  const [tabs, setTabs] = useState(FALLBACK_TABS);
  const [activeTab, setActiveTab] = useState('전체');
  const [search, setSearch] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [showTabManage, setShowTabManage] = useState(false);
  const [selected, setSelected] = useState(null);

  const perm = usePermissions();
  const { hasDetail, canWrite: canWriteMenu } = perm;
  const canWrite = canWriteMenu('contacts');
  const canDelete = hasDetail('contacts', 'delete_allowed');
  const canDownload = hasDetail('contacts', 'download_allowed');

  // Companies.js(업체관리)와 동일하게 카테고리 탭은 클라이언트 측 필터로 전환 — 탭 옆에
  // 전체 건수 배지를 붙이려면(요청사항: 상단탭 구성) 전체 목록을 한 번에 받아 각 탭 카운트를
  // 계산해야 하기 때문이다. 검색어만 서버에 전달한다.
  const load = () => {
    const params = {};
    if (search.trim()) params.q = search.trim();
    API.get('/contacts', { params }).then(r => setCards(r.data)).catch(() => {});
  };
  // 2026-09(2차) — 상단 탭이 하드코딩에서 사용자 관리(관리항목 버튼) 방식으로 바뀌면서, 탭
  // 목록도 서버(contact_menu_tabs)에서 받아온다. 활성 탭이 삭제/이름변경 됐을 수 있으니
  // loadTabs 이후 activeTab이 더 이상 존재하지 않으면 '전체'로 되돌린다.
  const loadTabs = () => {
    API.get('/contacts/categories').then(r => {
      setTabs(r.data);
      setActiveTab(prev => (prev === '전체' || r.data.some(t => t.name === prev)) ? prev : '전체');
    }).catch(() => {});
  };
  useEffect(() => { loadTabs(); }, []); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const refreshSelected = () => {
    load();
    if (selected) {
      API.get(`/contacts/${selected.id}`).then(r => setSelected(r.data)).catch(() => {});
    }
  };

  const displayedCards = activeTab === '전체' ? cards : cards.filter(c => (c.category || '기타') === activeTab);

  return (
    <div style={{ padding: 24 }}>

      {/* 헤더 — Companies.js(업체관리)와 동일한 타이틀/부제 + 우측 액션버튼 구성 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>📇 명함관리</h2>
          <p style={{ fontSize: 13, color: '#888', margin: '4px 0 0' }}>명함을 스캔·촬영해 AI가 자동 인식한 전자명함으로 관리합니다.</p>
        </div>
        {canWrite && (
          <div className="hdr-actions" style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowTabManage(true)}
              style={{ background: '#fff', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              📑 관리항목
            </button>
            <button onClick={() => setShowBatch(true)}
              style={{ background: '#fff', color: '#185FA5', border: '1px solid #185FA5', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              일괄 업로드
            </button>
            <button onClick={() => setShowRegister(true)}
              style={{ background: '#185FA5', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              + 명함 등록
            </button>
          </div>
        )}
      </div>

      {/* 탭메뉴(전체 + 관리항목에서 등록한 탭들) + 우측 검색 — Companies.js 상단탭 패턴과 동일 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16, paddingBottom: 10, flexWrap: 'wrap', gap: 10, borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {['전체', ...tabs.map(t => t.name)].map(cat => {
            const count = cat === '전체' ? cards.length : cards.filter(c => (c.category || '기타') === cat).length;
            const active = activeTab === cat;
            return (
              <button key={cat} onClick={() => setActiveTab(cat)}
                style={{ padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
                  borderBottom: active ? '2px solid #185FA5' : '2px solid #e5e7eb',
                  background: 'none', color: active ? '#185FA5' : '#888' }}>
                {cat}
                {count > 0 && (
                  <span style={{ fontSize: 11, marginLeft: 6, background: active ? '#185FA5' : '#e5e7eb',
                    color: active ? '#fff' : '#888', padding: '1px 7px', borderRadius: 20 }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 회사명/이름/부서 검색"
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, width: 240, boxSizing: 'border-box' }} />
      </div>

      {displayedCards.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#999', padding: '60px 0', fontSize: 14 }}>등록된 명함이 없습니다.</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {displayedCards.map(c => <CardThumb key={c.id} card={c} onClick={() => setSelected(c)} tabs={tabs} />)}
        </div>
      )}

      {showTabManage && (
        <TabManageModal tabs={tabs} canManage={canWrite} onClose={() => setShowTabManage(false)} onChanged={() => { loadTabs(); load(); }} />
      )}
      {showRegister && (
        <RegisterModal onClose={() => setShowRegister(false)} onSaved={load} categories={tabs} />
      )}
      {showBatch && (
        <BatchUploadModal onClose={() => setShowBatch(false)} onSaved={load} />
      )}
      {selected && (
        <DetailModal card={selected} onClose={() => setSelected(null)} onChanged={refreshSelected}
          canWrite={canWrite} canDelete={canDelete} canDownload={canDownload} tabs={tabs} />
      )}
    </div>
  );
}
